import React, { useCallback, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  IconButton,
  Paper,
  Chip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { PharmacyItemBatch } from '../ItemSearchField/itemSearchField.helper';
import useCurrency from '../../../../../components/CurrencyProvider';
import { formatDateDMY } from '../../../../../utils/dateUtils';

// --- Types ---
interface ExistingItem {
  itemCode: string;
  batchCode: string;
}

interface BatchSelectionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (batch: PharmacyItemBatch) => void;
  itemName: string;
  itemCode: string;
  batches: PharmacyItemBatch[];
  existingItems?: ExistingItem[];
}

// --- Component ---
const BatchSelectionModal: React.FC<BatchSelectionModalProps> = ({
  open,
  onClose,
  onConfirm,
  itemName,
  itemCode,
  batches,
  existingItems = [],
}) => {
  const [selectedBatch, setSelectedBatch] = useState<PharmacyItemBatch | null>(null);
  const { currencySymbol } = useCurrency();

  // --- Hook: Prevent memory leak on close ---
  useEffect(() => {
    if (!open) setSelectedBatch(null);
  }, [open]);

  // --- Util: Expiry Check ---
  const isBatchExpired = useCallback((expiryDate: string | null | undefined): boolean => {
    if (!expiryDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    if (isNaN(expiry.getTime())) return false;
    expiry.setHours(0, 0, 0, 0);
    return expiry < today;
  }, []);

  // --- Util: Duplicate in existing items? ---
  const isBatchDuplicate = useCallback((batchCode: string | null | undefined): boolean => {
    if (!batchCode) return false;
    return existingItems.some(
      (item) => item.itemCode === itemCode && item.batchCode === batchCode
    );
  }, [existingItems, itemCode]);

  // --- Batch Validation logic ---
  const getBatchValidation = useCallback((batch: PharmacyItemBatch | null | undefined) => {
    if (!batch) {
      return { isValid: false, message: 'Invalid batch', color: 'error' as const };
    }
    const isHold = batch.is_hold ?? false;
    const isExpired = isBatchExpired(batch.expiry_date);
    const availableStock = Number(batch.available_stock) || 0;
    const isDuplicate = isBatchDuplicate(batch.batch_number);
    if (isHold) {
      return { isValid: false, message: 'On hold', color: 'warning' as const };
    }
    if (isDuplicate) {
      return { isValid: false, message: 'Already added', color: 'warning' as const };
    }
    if (isExpired) {
      return { isValid: false, message: 'Expired', color: 'error' as const };
    }
    if (availableStock <= 0) {
      return { isValid: false, message: 'Out of stock', color: 'error' as const };
    }
    return { isValid: true, message: '', color: undefined };
  }, [isBatchExpired, isBatchDuplicate]);

  // --- Batches with validation ---
  const categorizedBatches = useMemo(() => {
    if (!Array.isArray(batches)) return [];
    return batches
      .filter((batch): batch is PharmacyItemBatch => !!batch)
      .map((batch) => ({
        ...batch,
        validation: getBatchValidation(batch),
      }));
  }, [batches, getBatchValidation]);

  // --- HandleConfirm ---
  const handleConfirm = useCallback(() => {
    if (selectedBatch) {
      const validation = getBatchValidation(selectedBatch);
      if (!validation.isValid) return;
      onConfirm(selectedBatch);
      setSelectedBatch(null);
    }
  }, [getBatchValidation, onConfirm, selectedBatch]);

  // --- HandleClose ---
  const handleClose = useCallback(() => {
    setSelectedBatch(null);
    onClose();
  }, [onClose]);

  // --- Formatters ---
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    const formatted = formatDateDMY(dateString);
    return formatted || '-';
  };

  const formatCurrency = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    const numValue = Number(value);
    if (isNaN(numValue)) return '-';
    return numValue.toFixed(2);
  };

  // --- JSX ---
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: 2 },
          // @ts-expect-error MUI does not type data-testid, but it will be forwarded at runtime
          'data-testid': 'batch-selection-dialog',
        }
      }}
      aria-labelledby="batch-selection-title"
      id="batch-selection-dialog"
    >
      <DialogTitle
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}
        id="batch-selection-title"
        data-testid="batch-selection-title"
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Available Batches
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Item Name: <span data-testid="batch-selection-item-name">{itemName}</span>
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          size="small"
          aria-label="Close batch selection"
          id="close-batch-selection"
          data-testid="close-batch-selection"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
          Available Batches
        </Typography>

        {categorizedBatches.length === 0 ? (
          <Box
            sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}
            data-testid="no-batch-available"
          >
            <Typography variant="body2" color="text.secondary">
              No batches available for this item
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            data-testid="batch-table-container"
          >
            <Table size="small" aria-label="Available batches table">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 600 }}>Expiry Date</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Batch Code</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Available Qty</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>UOM</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Purchase Rate</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>MRP</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categorizedBatches.map((item, index) => {
                  const { validation, ...batch } = item;
                  // Prefer id, then batch_number, else fallback
                  const batchId: string =
                    (batch.id ? String(batch.id) : batch.batch_number) || `batch-${index}`;
                  const isDisabled = !validation.isValid;
                  const isSelected = !!(
                    selectedBatch &&
                    (selectedBatch.id === batch.id || selectedBatch.batch_number === batch.batch_number)
                  );
                  return (
                    <TableRow
                      key={batchId}
                      hover={!isDisabled}
                      onClick={() => !isDisabled && setSelectedBatch(batch)}
                      sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'inherit',
                        opacity: isDisabled ? 0.5 : 1,
                      }}
                      id={`batch-row-${batchId}`}
                      data-testid={`batch-row-${batchId}`}
                      tabIndex={isDisabled ? -1 : 0}
                      aria-disabled={isDisabled}
                    >
                      <TableCell padding="checkbox" align="center">
                        <Radio
                          checked={isSelected}
                          onChange={() => !isDisabled && setSelectedBatch(batch)}
                          size="small"
                          disabled={isDisabled}
                          slotProps={{
                            input: {
                              'aria-label': `Select batch ${batch.batch_number || batchId}`,
                              id: `batch-radio-${batchId}`,
                            }
                          }}
                          data-testid={`batch-radio-${batchId}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {formatDate(batch.expiry_date)}
                          {validation.message && (
                            <Chip
                              label={validation.message}
                              color={validation.color}
                              size="small"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                              id={`batch-chip-${batchId}`}
                              data-testid={`batch-chip-${batchId}`}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{batch.batch_number ?? '-'}</TableCell>
                      <TableCell>{Number(batch.available_stock) || 0}</TableCell>
                      <TableCell>{batch.available_uom_name ?? '-'}</TableCell>
                      <TableCell>
                        {currencySymbol ?? ''}
                        {formatCurrency(batch.base_cost)}
                      </TableCell>
                      <TableCell>
                        {currencySymbol ?? ''}
                        {formatCurrency(batch.base_mrp)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          color="inherit"
          id="batch-cancel-btn"
          data-testid="batch-cancel-btn"
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="primary"
          id="batch-add-btn"
          data-testid="batch-add-btn"
          disabled={
            !selectedBatch ||
            (selectedBatch && !getBatchValidation(selectedBatch).isValid)
          }
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BatchSelectionModal;